import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import Button from 'app/components/base/Button'
import Dialog from 'app/components/base/Dialog'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import GeosuggestField from 'app/components/fields/GeosuggestField'
import useFormErrorHandler from 'app/hooks/useFormErrorHandler'
import Alert from 'app/services/Alert'
import { isErrorHandlerType } from 'app/services/ErrorHandler'
import type { AddressObject } from 'app/services/Geosuggest/Geosuggest.types'
import { useUpdateShippingAddressMutation } from 'app/services/Query/user/user.mutation'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'
import { transformAddressToDTO } from 'app/utils/helpers/transforms.helper'

interface AddAddressFV {
  address: Omit<GeosuggestValue, 'address'> & {
    address: AddressObject
  }
}

interface AddAddressDialogProps {
  open: boolean
  onClose: () => void
  calculateShippingCost: (address: Partial<Address>) => Promise<void>
}

const AddAddressDialog: RFC<AddAddressDialogProps> = ({
  open,
  onClose,
  calculateShippingCost
}) => {
  const intl = useIntl()
  const methods = useForm<AddAddressFV>()

  const { mutateAsync } = useUpdateShippingAddressMutation()

  const { isSubmitting } = methods.formState
  const handleFormError = useFormErrorHandler(methods.setError)

  const onSubmit = async (values: AddAddressFV) => {
    try {
      const address = transformAddressToDTO(values.address.address)
      await mutateAsync({ addresses_attributes: [address] })
      Alert.success(intl.formatMessage({ defaultMessage: 'Saved' }))

      await calculateShippingCost({ id: '', ...address })
      onClose()
    } catch (error) {
      if (isErrorHandlerType(error)) handleFormError(error?.data)
    }
  }

  return (
    <Dialog
      open={open}
      titleSx={{ alignSelf: 'center' }}
      title={<FormattedMessage defaultMessage="Add Address" />}
      dialogSx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '527px',
            py: 5,
            px: { xs: 2, sm: 8 },
            borderRadius: 2
          },
          '.MuiDialogContent-root': {
            px: { xs: 0, sm: 3 }
          }
        }
      }}
      onClose={onClose}
      actions={
        <Button
          size="small"
          onClick={methods.handleSubmit(onSubmit)}
          loading={isSubmitting}
          sx={{
            mx: { xs: 0, sm: !IS_NATIVE_APP ? 3 : 0 },
            ...(IS_NATIVE_APP && { flex: 1 })
          }}
        >
          <FormattedMessage defaultMessage="Submit" />
        </Button>
      }
    >
      <FormProvider {...methods}>
        <Stack spacing={3}>
          <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <Text sx={{ textAlign: 'center' }} color="text.third">
              <FormattedMessage defaultMessage="This address will be saved to your profile details. You always can change." />
            </Text>
          </Stack>
          <Stack direction="column" spacing={0.3}>
            <GeosuggestField
              name="address"
              // options={{ types: ['address'] }}
              label={intl.formatMessage({
                defaultMessage: 'Type Address'
              })}
            />

            <Text variant="subtitle2" sx={{ color: 'text.secondary' }}>
              <FormattedMessage defaultMessage="E.g. 123 Example Street, London, ZZ99 9ZZ" />
            </Text>
          </Stack>
        </Stack>
      </FormProvider>
    </Dialog>
  )
}

export default AddAddressDialog
